import { HttpErrorResponse } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { CanActivateFn, Params } from '@angular/router';
import { isNullOrUndefined } from '@swissfex/sfx-commons';
import { takeLast, takeWhile } from 'rxjs';

import { environment } from '../../../environments/environment';
import { LOGIN_QUERY, LOGIN_QUERY_LOGOUT_REASON_MAP, LOGOUT_REASON, MAIN_ROUTES } from '../../constants';
import { MadTranslateService } from '../../mad-translate/services/mad-translate.service';
import { AuthenticationService } from '../../projects/services/authentication.service';
import { IApplicationUser } from '../models/IApplicationUser';
import { RouterService } from '../services/router.service';
import { SESSION_STORAGE_KEY, SessionStorageService } from '../services/session-storage.service';
import { TranslateRouterService } from '../services/translate-router.service';

@Service()
class AuthenticatedGuardService {
    private readonly authSvc = inject(AuthenticationService);
    private readonly madTranslateSvc = inject(MadTranslateService);
    private readonly routerSvc = inject(RouterService);
    private readonly sessionStorageSvc = inject(SessionStorageService);
    private readonly translateRouterSvc = inject(TranslateRouterService);

    public canActivate(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.authSvc.authStatus
                .pipe(
                    takeWhile((authData) => !authData, true),
                    takeLast(1)
                )
                .subscribe(async (authData) => {
                    const queryParams: Params = {
                        [LOGIN_QUERY.RETURN_URL]: this.routerSvc.getValidReturnUrl(
                            `${location.pathname}${location.search}`
                        )
                    };

                    try {
                        if (authData.isAuthenticated) {
                            const currentUser = await this.authSvc.getCurrentUser();
                            let shouldContinueActivating = true;

                            if (currentUser) {
                                if (currentUser.userType) {
                                    let location = window.location.toString();
                                    const host = window.location.host;
                                    if (host !== environment.originsPerUserType[currentUser.userType]) {
                                        location = location.replace(
                                            host,
                                            environment.originsPerUserType[currentUser.userType]
                                        );
                                        window.location.href = location;
                                        return;
                                    }
                                }

                                shouldContinueActivating =
                                    await this.handleAuthenticatedUserLanguageChanges(currentUser);
                            }
                            resolve(shouldContinueActivating);
                        } else {
                            if (queryParams[LOGIN_QUERY.RETURN_URL]) {
                                this.sessionStorageSvc.set(
                                    SESSION_STORAGE_KEY.RETURN_URL_KEY,
                                    queryParams[LOGIN_QUERY.RETURN_URL]
                                );
                            }

                            await this.authSvc.login(this.authSvc.currentIdentityProvider);
                            resolve(false);
                        }
                    } catch (ex) {
                        console.log(ex);
                        if (ex instanceof HttpErrorResponse) {
                            if (!isNullOrUndefined(ex.status)) {
                                const loginQuery =
                                    LOGIN_QUERY_LOGOUT_REASON_MAP[ex.status.toString()] || LOGIN_QUERY.ERROR;
                                queryParams[loginQuery] = ex.status;
                            } else {
                                const reason = LOGOUT_REASON.FORBIDDEN;
                                queryParams[LOGIN_QUERY_LOGOUT_REASON_MAP[reason]] = reason;
                            }
                        }
                        await this.routerSvc.navigateToLogout({ queryParams });
                        resolve(false);
                    }
                });
        });
    }

    private async handleAuthenticatedUserLanguageChanges(currentUser: IApplicationUser): Promise<boolean> {
        const language = this.madTranslateSvc.getAppropriateUiLanguage(currentUser);
        const urlLanguage = this.madTranslateSvc.getSelectedLanguage();
        if (language !== urlLanguage) {
            const pathName = await this.translateRouterSvc.parseAndTranslateUrl(language, location.pathname);
            const translatedLoggingInRoute = this.routerSvc.translateRoute([MAIN_ROUTES.LOGGING_IN], null, true);

            if (pathName === translatedLoggingInRoute) {
                const returnUrlPathName = this.sessionStorageSvc.get<string>(SESSION_STORAGE_KEY.RETURN_URL_KEY);
                if (returnUrlPathName) {
                    const newReturnUrlPathName = await this.translateRouterSvc.parseAndTranslateUrl(
                        language,
                        returnUrlPathName
                    );
                    this.sessionStorageSvc.set(SESSION_STORAGE_KEY.RETURN_URL_KEY, newReturnUrlPathName);
                }
            }
            window.location.pathname = pathName;
            return false; // Prevent further navigation, stop app from processing
        } else {
            this.madTranslateSvc.setNonRouterInvasiveServicesLanguage(language);
            return true;
        }
    }
}

export const authenticatedGuard: CanActivateFn = () => {
    return inject(AuthenticatedGuardService).canActivate();
};
